import { getUTMSources } from "@/components/listings/sidebar/lib/utm-content";
import { BrokerAvatar } from "@/components/brokers/broker-avatar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { H4 } from "@/components/ui/heading";
import { Icon } from "@/components/ui/icon";
import { Textarea } from "@/components/ui/textarea";
import { useInquirySender } from "@/hooks/use-inquiry-sender";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { useListingAnalytics } from "@/hooks/use-listing-analytics";
import { api } from "@/lib/api-client";
import { getListingTitle } from "@/lib/string-utils";
import type { DetailedListing } from "@/lib/types";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { z } from "zod/v4";
import * as metaPixel from "@/lib/meta-pixel";
import { useSearchParams } from "next/navigation";

const formSchema = z.object({
  message: z.string().nonempty(),
});

type FormValues = z.infer<typeof formSchema>;

export type ListingContactFormProps = {
  listing: DetailedListing;
  onSendComplete?: () => Promise<any>;
};

export function ListingContactForm({
  listing,
  onSendComplete,
}: ListingContactFormProps) {
  const t = useTranslations();
  const searchParams = useSearchParams();

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(formSchema),
    defaultValues: {
      message: t(
        "saas.listing.sidebar.contactLoggedIn.form.fields.message.placeholder",
        {
          listingTitle: getListingTitle(t, listing),
        },
      ),
    },
  });

  const { user, loaded, senderError, delegated } = useInquirySender();
  const { trackContactClick } = useListingAnalytics();

  const canInquireQuery = api.listings.canInquire.useQuery(
    { listingId: listing.id },
    {
      enabled:
        !!user &&
        user.id !== listing.ownerId &&
        user.id !== listing.sellerAgentId,
    },
  );

  const sendMessageMutation = api.listings.sendMessage.useMutation();

  const { displayErrorToast, formatError } = useErrorFormatter();
  const profileName = user?.name ?? user?.email ?? "Your profile";

  async function onSubmit({ message }: FormValues) {
    if (
      !user ||
      user.id === listing.ownerId ||
      user.id === listing.sellerAgentId
    ) {
      // seller and seller agent shouldn't be able to send a message
      return;
    }

    try {
      await trackContactClick();
    } catch (_err) {
      // ignore
    }

    const leadSources = getUTMSources(listing.id);
    const currentUTMContent = searchParams.get("utm_content");

    if (currentUTMContent && !leadSources.includes(currentUTMContent)) {
      leadSources.push(currentUTMContent);
    }

    try {
      await sendMessageMutation.mutateAsync({
        id: listing.id,
        message,
        leadSources,
      });

      if (leadSources.length > 0) {
        metaPixel.event("Lead");
      }

      onSendComplete?.();
    } catch (err) {
      console.error(err);
      displayErrorToast(err);
      form.setError("message", {
        message: formatError(err),
      });
    }
  }

  function handleTextareaFocus(e: React.FocusEvent<HTMLTextAreaElement>) {
    if (e.target.value === form.control._defaultValues.message) {
      form.setValue("message", "", { shouldValidate: false });
    }
  }

  function handleTextareaBlur(e: React.FocusEvent<HTMLTextAreaElement>) {
    if (e.target.value.trim() === "") {
      form.resetField("message");
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col p-5 gap-3.5 w-full"
      >
        <H4 className="text-base font-semibold">
          {t("saas.listing.sidebar.contactLoggedIn.title")}
        </H4>

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>What should the broker know?</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={form.control._defaultValues.message}
                  onFocus={handleTextareaFocus}
                  onBlur={handleTextareaBlur}
                  className="min-h-[80px]"
                  disabled={canInquireQuery.data?.allowed === false}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {canInquireQuery.data?.allowed === false &&
          canInquireQuery.data.reason === "BROKER_BLOCK" && (
            <Alert variant="destructive">
              <AlertDescription>
                {t("saas.inquiryRestrictions.brokerNotAccepting")}
              </AlertDescription>
            </Alert>
          )}

        <div className="flex flex-col gap-3">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Sending as
          </div>

          {user && (
            <div className="border border-border rounded-lg p-3 flex items-center gap-3">
              <BrokerAvatar
                id={user.id}
                name={profileName}
                image={user.image}
                isVerified={false}
                showVerified={false}
                size="sm"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {profileName}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {user.email}
                </div>
              </div>
              {!delegated && (
                <Button variant="ghost" size="sm" className="h-8 px-2" asChild>
                  <Link href="/my-profile" aria-label="Edit your profile">
                    <Icon icon="Pencil2" />
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>

        {senderError && (
          <p role="alert">
            Inquiry sender is unavailable. Choose an authorized workspace and
            try again.
          </p>
        )}
        <Button
          type="submit"
          className="w-full h-11 bg-foreground text-background hover:bg-foreground/90"
          disabled={
            !loaded ||
            !user ||
            form.formState.isSubmitting ||
            user?.id === listing.ownerId ||
            user?.id === listing.sellerAgentId ||
            canInquireQuery.data?.allowed === false
          }
        >
          <Icon icon="Send" />
          {t("saas.listing.sidebar.contactLoggedIn.form.submit")}
        </Button>

        <p className="text-xs text-muted-foreground text-center leading-relaxed">
          Your message goes directly to the broker. Most brokers respond within
          24 hours.
        </p>
      </form>
    </Form>
  );
}
